"use client"
import { initAnalytics } from "@opensea/analytics"
import { ChainsProvider, MarketsProvider } from "@opensea/app-kit"
import { GraphQLProvider } from "@opensea/graphql"
import { DEFAULT_TIMEZONE } from "@opensea/i18n"
import type { CurrencyPreference, Locale, Persona } from "@opensea/navigation"
import { CurrencyProvider, PersonaProvider } from "@opensea/navigation"
import { ThemeProvider } from "@opensea/next-themes"
import {
  MediaContextProvider,
  SizeProvider,
  ToastProvider,
} from "@opensea/ui-kit"
import { captureException } from "@sentry/nextjs"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import type { IToggle } from "@unleash/nextjs"
import type { NextIntlClientProvider } from "next-intl"
import { NuqsAdapter } from "nuqs/adapters/next/app"
import type { ComponentProps } from "react"
import { useState } from "react"
import type { CreateConnectorFn } from "wagmi"
import { ItemViewModalProvider } from "@/components/items/item-page/ItemViewModal/ItemViewModalProvider"

import { OnboardingGuard } from "@/components/onboarding/OnboardingGuard"
import { ProfileSettingsProvider } from "@/components/profile/ProfileSettingsProvider"
import { IntercomProvider } from "@/components/support/IntercomProvider"
import { AccountBannedGuard } from "@/components/trustandsafety/AccountBannedGuard/AccountBannedGuard"
import { BUILD_ID } from "@/constants/env"
import { initDatadogRum } from "@/lib/integrations/datadog"
import { errorHandler as graphQLErrorHandler } from "../../lib/graphql"
import { FlagsProvider } from "../FlagsProvider"
import { IntlProvider } from "../IntlProvider"
import { RiveProvider } from "../RiveProvider"
import { WalletProvider } from "../WalletProvider"
import type { Chain, Currency, Market } from "./client-providers-query"

initDatadogRum().catch((error: unknown) => captureException(error))
initAnalytics({
  build: BUILD_ID,
}).catch((error: unknown) => {
  console.error(error)
  captureException(error)
})

type ClientProvidersProps = {
  toggles: IToggle[]
  children: React.ReactNode
  locale: Locale
  persona: Persona
  chains: Chain[]
  markets: Market[]
  currency: CurrencyPreference
  currencies: Currency[]
  connectors?: CreateConnectorFn[]
  messages: ComponentProps<typeof NextIntlClientProvider>["messages"]
}

export function ClientProviders({
  children,
  toggles,
  persona,
  chains,
  currency,
  currencies,
  markets,
  locale,
  connectors,
  messages,
}: ClientProvidersProps) {
  const [queryClient] = useState(() => new QueryClient({}))

  return (
    <ChainsProvider chains={chains}>
      <NuqsAdapter>
        <FlagsProvider toggles={toggles}>
          {/* Default to dark mode given users will be coming from the waitlist */}
          <ThemeProvider theme="dark">
            <IntlProvider
              locale={locale}
              messages={messages}
              timeZone={DEFAULT_TIMEZONE}
            >
              <MediaContextProvider>
                <SizeProvider>
                  <QueryClientProvider client={queryClient}>
                    <GraphQLProvider errorHandler={graphQLErrorHandler}>
                      <ToastProvider>
                        <RiveProvider>
                          <WalletProvider
                            chains={chains}
                            connectors={connectors}
                            initialCurrencies={currencies}
                          >
                            <CurrencyProvider currency={currency}>
                              <PersonaProvider persona={persona}>
                                <MarketsProvider markets={markets}>
                                  <ProfileSettingsProvider>
                                    <ItemViewModalProvider>
                                      <AccountBannedGuard>
                                        <OnboardingGuard>
                                          <IntercomProvider>
                                            {children}
                                          </IntercomProvider>
                                        </OnboardingGuard>
                                      </AccountBannedGuard>
                                    </ItemViewModalProvider>
                                  </ProfileSettingsProvider>
                                </MarketsProvider>
                              </PersonaProvider>
                            </CurrencyProvider>
                          </WalletProvider>
                        </RiveProvider>
                      </ToastProvider>
                    </GraphQLProvider>
                  </QueryClientProvider>
                </SizeProvider>
              </MediaContextProvider>
            </IntlProvider>
          </ThemeProvider>
        </FlagsProvider>
      </NuqsAdapter>
    </ChainsProvider>
  )
}
